import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Tabs,
  Tab,
  Box,
  Chip,
  LinearProgress,
  Paper,
  IconButton,
  Divider
} from '@mui/material';
import { CalendarToday, Launch } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowUpRightFromSquare,
  faFilePdf,
  faFileAlt,
  faImage,
  faVideo,
  faMusic,
  faTable,
  faFilePowerpoint,
  faFileArchive,
  faCode,
  faFile
} from '@fortawesome/free-solid-svg-icons';
import { getFileInfo, detectFileTypeAdvanced, getEnhancedFileInfo } from '../utils/fileTypeDetector';
import '../styles/ContractDetails.css';

const ClientFinalDeliverables = () => {
  const { contractId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [fileTypeCache, setFileTypeCache] = useState({});

  // Filter options - focusing on the most common deliverable types
  const filterOptions = ['All', 'Image', 'Video', 'PDF'];

  // Helper function to format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    fetchFinalDeliverables();
  }, [contractId]);

  const fetchFinalDeliverables = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/client-dashboard/tasks/all/${contractId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const allTasks = await response.json();
      console.log('All tasks data:', allTasks);
      
      // Filter for tasks with final deliverables only
      const deliverableTasks = allTasks.filter(task => {
        const hasFinalDeliverable = task.link_to_final_deliverable && task.link_to_final_deliverable.trim() !== '';
        const notDeleted = !task.is_deleted;
        const notArchived = !task.is_archived;
        const isDelivered = task.status && task.status.toLowerCase() === 'delivered';
        
        console.log(`Task "${task.name}": hasFinalDeliverable=${hasFinalDeliverable}, delivered=${isDelivered}, deleted=${task.is_deleted}, archived=${task.is_archived}`);
        
        return hasFinalDeliverable && notDeleted && notArchived && isDelivered;
      });
      
      console.log('Final deliverable tasks:', deliverableTasks);
      console.log('Final deliverable tasks count:', deliverableTasks.length);

      // Enhance with file type detection
      const enhancedTasks = await Promise.all(
        deliverableTasks.map(async (task) => {
          const fileInfo = await getEnhancedFileInfoWithTaskName(task.link_to_final_deliverable, task.name);
          return {
            ...task,
            fileInfo
          };
        })
      );

      setTasks(enhancedTasks);
    } catch (err) {
      console.error('Error fetching final deliverables:', err);
      setError('Failed to load final deliverables');
    } finally {
      setLoading(false);
    }
  };

  const getEnhancedFileInfoWithTaskName = async (url, taskName) => {
    if (fileTypeCache[url]) return fileTypeCache[url];
    
    // First try the enhanced detection with task name
    const enhancedInfo = getEnhancedFileInfo(url, taskName);
    
    if (enhancedInfo.needsAdvancedDetection) {
      try {
        const advancedType = await detectFileTypeAdvanced(url);
        const finalInfo = {
          type: advancedType,
          icon: getFileTypeIcon(advancedType),
          label: getFileTypeLabel(advancedType),
          color: getFileTypeColor(advancedType)
        };
        setFileTypeCache(prev => ({ ...prev, [url]: finalInfo }));
        return finalInfo;
      } catch (error) {
        console.debug('Advanced file type detection failed:', error);
      }
    }
    
    setFileTypeCache(prev => ({ ...prev, [url]: enhancedInfo }));
    return enhancedInfo;
  };

  // Import the helper functions from the utility
  const getFileTypeIcon = (fileType) => {
    const iconMap = {
      'pdf': faFilePdf,
      'document': faFileAlt,
      'image': faImage,
      'video': faVideo,
      'audio': faMusic,
      'spreadsheet': faTable,
      'presentation': faFilePowerpoint,
      'archive': faFileArchive,
      'code': faCode,
      'unknown': faFile
    };
    return iconMap[fileType] || iconMap.unknown;
  };

  const getFileTypeLabel = (fileType) => {
    const labelMap = {
      'pdf': 'PDF Document',
      'document': 'Document',
      'image': 'Image',
      'video': 'Video',
      'audio': 'Audio',
      'spreadsheet': 'Spreadsheet',
      'presentation': 'Presentation',
      'archive': 'Archive',
      'code': 'Code',
      'unknown': 'File'
    };
    return labelMap[fileType] || labelMap.unknown;
  };

  const getFileTypeColor = (fileType) => {
    const colorMap = {
      'pdf': '#8A42EC',        // Brand Purple (PDFs)
      'document': '#5B21B6',   // Deep Purple (Documents)
      'image': '#059669',      // Professional Green (Images)
      'video': '#7C2D92',      // Dark Purple (Videos)
      'audio': '#6366F1',      // Indigo (Audio)
      'spreadsheet': '#16A34A', // Professional Green (Spreadsheets)
      'presentation': '#8A42EC', // Brand Purple (Presentations)
      'archive': '#6B7280',    // Professional Gray (Archives)
      'code': '#374151',       // Dark Gray (Code)
      'unknown': '#6B7280'     // Professional Gray (Unknown)
    };
    return colorMap[fileType] || colorMap.unknown;
  };

  // Filter tasks based on selected file type
  const filteredTasks = tasks.filter(task => {
    if (selectedFilter === 'All') return true;
    
    const fileType = task.fileInfo?.type?.toLowerCase();
    const filterType = selectedFilter.toLowerCase();
    
    return fileType === filterType;
  }).sort((a, b) => {
    // Sort by date done descending (newest first)
    if (!a.date_done && !b.date_done) return 0;
    if (!a.date_done) return 1;
    if (!b.date_done) return -1;
    return new Date(b.date_done) - new Date(a.date_done);
  });

  const renderDeliverableCard = (task) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={task.task_id}>
      <Card 
        sx={{ 
          height: 280,
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
          },
          cursor: 'pointer'
        }}
        onClick={() => window.open(task.link_to_final_deliverable, '_blank')}
      >
        {/* File Type Icon Header */}
        <Box 
          sx={{ 
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: task.fileInfo?.color || '#6B7280',
            color: 'white',
            position: 'relative'
          }}
        >
          <FontAwesomeIcon 
            icon={task.fileInfo?.icon || faFile} 
            style={{ 
              fontSize: '40px',
              width: '40px',
              height: '40px'
            }}
          />
          <IconButton
            sx={{ 
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)'
              }
            }}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(task.link_to_final_deliverable, '_blank');
            }}
          >
            <Launch fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography 
            variant="subtitle1" 
            fontWeight={600} 
            sx={{ 
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              height: '2.6em'
            }}
          >
            {task.name}
          </Typography>

          {task.external_description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.4,
                flex: 1
              }}
            >
              {task.external_description}
            </Typography>
          )}

          <Box sx={{ mt: 'auto' }}>
            <Chip 
              icon={<CalendarToday sx={{ color: '#8A42EC' }} />} 
              label={`Completed: ${formatDate(task.date_done)}`} 
              size="small" 
              sx={{ 
                px: 1, 
                py: 1,
                '& .MuiChip-label': {
                  fontSize: '12px'
                }
              }} 
            />
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mt: 1,
                color: task.fileInfo?.color || '#6B7280',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {task.fileInfo?.label || 'File'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      {/* File Type Filter Tabs */}
      <Card className="details-card" style={{ marginBottom: '20px' }}>
        <Tabs
          value={selectedFilter}
          onChange={(event, newValue) => setSelectedFilter(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {filterOptions.map((filter) => (
            <Tab
              key={filter}
              label={filter}
              value={filter}
            />
          ))}
        </Tabs>
      </Card>

      {/* Loading State */}
      {loading && <LinearProgress style={{ marginBottom: '20px' }} />}

      {/* Error State */}
      {error && (
        <Card style={{ padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}

      {/* Deliverables Grid */}
      {!loading && !error && (
        <Card className="details-card">
          <CardHeader 
            title={`${filteredTasks.length} deliverable${filteredTasks.length !== 1 ? 's' : ''}`}
          />
          <Divider />
          <CardContent>
            {filteredTasks.length > 0 ? (
              <Grid container spacing={3}>
                {filteredTasks.map(renderDeliverableCard)}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
                <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
                  No final deliverables found
                </Typography>
                <Typography variant="body2" style={{ color: '#999' }}>
                  {selectedFilter !== 'All' 
                    ? `No ${selectedFilter.toLowerCase()} deliverables found.`
                    : 'No completed tasks with final deliverables.'
                  }
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ClientFinalDeliverables; 